import { useLayoutEffect } from 'react';
import { getAnalytics } from 'src/lib/analytics/analytics.service';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import type { tSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.types';

type tSurveyMetadataOptions = Pick<
  tSubscriptionContext,
  'subscription' | 'hasSubscriptionData' | 'isLoadingSubscription'
>;

// PMF's PostHog element condition is html[data-pmf-metadata-ready="true"].
// Keep the native SDK's audience, recurrence and completion checks intact.
export function useSurveyMetadataReadiness(options: tSurveyMetadataOptions) {
  const { currentUser, currentUserRole, hasSession } = useUserContext();
  const {
    currentWorkspaceId,
    currentWorkspace,
    workspaceInfo,
    workspaceInfoLoadedForId,
    isLoadingWorkspaceInfo,
  } = useWorkspaceContext();
  const role = currentUser?.authId
    ? workspaceInfo?.users[currentUser.authId]?.role
    : undefined;
  const plan = options.subscription.planCode;
  const ready = !!(
    hasSession &&
    currentUser?.uuid &&
    currentWorkspaceId &&
    currentWorkspace?.id === currentWorkspaceId &&
    workspaceInfoLoadedForId === currentWorkspaceId &&
    !isLoadingWorkspaceInfo &&
    role &&
    role === currentUserRole &&
    options.hasSubscriptionData &&
    !options.isLoadingSubscription &&
    plan
  );

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.removeAttribute('data-pmf-metadata-ready');
    if (!ready || !currentUser?.uuid || !currentWorkspaceId) return;

    const analytics = getAnalytics();
    analytics.identify(currentUser.uuid);
    analytics.register({
      workspace_id: currentWorkspaceId,
      user_role: role,
      workspace_plan: plan,
    });
    analytics.group('workspace', currentWorkspaceId);
    root.setAttribute('data-pmf-metadata-ready', 'true');
    return () => root.removeAttribute('data-pmf-metadata-ready');
  }, [ready, currentUser?.uuid, currentWorkspaceId, role, plan]);
}
